import { Fragment, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { api, useAuth, Perm } from '@shop/api-client'
import { formatMoney, formatQty } from '@shop/shared'
import { Banner, Empty, ErrorBanner, Field, Select, Spinner } from '@shop/ui'

/**
 * التقاريرُ السبعة — <b>مدًى واحدٌ يعلوها كلَّها، وجدولٌ يُقرأ ويُصدَّر</b>.
 *
 * ⚠ <b>والمدى يومُ عملٍ محلّيّ لا يومُ UTC</b> (ب-١): ما يُكتب هنا
 *   «٢٠٢٦-٠٩-١٧» يعني يومَ عملٍ في عمّان، والخادمُ يحوّله إلى لحظتين بـUTC.
 *   <b>وبيعةُ الواحدة صباحًا تقع في يومها لا في الذي قبله.</b>
 *
 * ⚠ <b>والحدُّ الأقصى ٣٦٦ يومًا يفرضه الخادم</b> (ب-٧) — وهذه الشاشةُ تمنع
 *   الوصولَ إلى رفضه، <b>ولا تُغني عنه</b>: الـAPI مفتوحةٌ لمن يملك رمزًا.
 */
type Tab = 'sales' | 'invoices' | 'top' | 'margin' | 'stock' | 'shifts' | 'purchases'

const TABS: { id: Tab; label: string; needsCost?: boolean }[] = [
  { id: 'sales', label: 'المبيعات' },
  { id: 'invoices', label: 'الفواتير' },
  { id: 'top', label: 'الأكثرُ بيعًا' },
  { id: 'margin', label: 'الربحُ والهامش', needsCost: true },
  { id: 'stock', label: 'الراكدُ والسالب' },
  { id: 'shifts', label: 'الورديّات' },
  { id: 'purchases', label: 'المشتريات' },
]

/**
 * لحظةٌ بتوقيت عمّان — <b>لا بتوقيت متصفّح القارئ</b>.
 *
 * ⚠ فمالكٌ يقرأ الكشفَ من خارج الأردنّ يجب أن يرى ساعةَ المحلّ، وإلّا
 *   بدت فاتورةُ التاسعة مساءً فاتورةَ السادسة — ولا تُطابَق بالدرج.
 */
function formatMoment(iso: string) {
  return new Intl.DateTimeFormat('ar-JO', {
    timeZone: 'Asia/Amman',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(iso))
}

function today() {
  return new Date().toISOString().slice(0, 10)
}

function daysAgo(days: number) {
  const date = new Date()

  date.setDate(date.getDate() - days)

  return date.toISOString().slice(0, 10)
}

export default function Reports() {
  const { session } = useAuth()

  const has = (code: string) => session?.me?.permissions.includes(code) ?? false

  const seesCost = has(Perm.costView)
  const canExport = has(Perm.reportExport)

  const [tab, setTab] = useState<Tab>('sales')
  const [from, setFrom] = useState(daysAgo(29))
  const [to, setTo] = useState(today())
  const [staleDays, setStaleDays] = useState(30)

  const span = Math.round(
    (Date.parse(to) - Date.parse(from)) / 86_400_000,
  ) + 1

  const tooLong = span > 366
  const inverted = span <= 0

  const visible = TABS.filter((t) => !t.needsCost || seesCost)

  return (
    <div className="mx-auto max-w-6xl space-y-4 p-4">
      <h1 className="text-xl font-bold">التقارير</h1>

      <div className="flex flex-wrap gap-1 border-b border-line pb-2">
        {visible.map((t) => (
          <button
            key={t.id}
            type="button"
            className={
              'rounded-lg px-3 py-2 text-sm ' +
              (tab === t.id ? 'bg-accent text-white font-semibold' : 'hover:bg-surface-raised')
            }
            onClick={() => setTab(t.id)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab !== 'stock' && (
        <div className="flex flex-wrap items-end gap-3 rounded-xl border border-line bg-surface-raised p-3">
          <Field label="من">
            <input
              className="min-h-10 rounded-lg border border-line bg-surface px-3"
              type="date"
              value={from}
              onChange={(event) => setFrom(event.target.value)}
            />
          </Field>

          <Field label="إلى">
            <input
              className="min-h-10 rounded-lg border border-line bg-surface px-3"
              type="date"
              value={to}
              onChange={(event) => setTo(event.target.value)}
            />
          </Field>

          <div className="text-xs text-ink-soft">
            {inverted ? '—' : `${span} يومًا`}
          </div>

          <div className="grow" />

          {[
            { label: 'اليوم', from: today(), to: today() },
            { label: 'آخرُ ٧', from: daysAgo(6), to: today() },
            { label: 'آخرُ ٣٠', from: daysAgo(29), to: today() },
          ].map((preset) => (
            <button
              key={preset.label}
              type="button"
              className="rounded-lg border border-line px-3 py-1.5 text-xs"
              onClick={() => {
                setFrom(preset.from)
                setTo(preset.to)
              }}
            >
              {preset.label}
            </button>
          ))}
        </div>
      )}

      {tab === 'stock' && (
        <div className="flex items-end gap-3 rounded-xl border border-line bg-surface-raised p-3">
          <Field label="يُعدّ راكدًا بعد">
            <Select value={String(staleDays)} onChange={(v) => setStaleDays(Number(v))}>
              {[7, 14, 30, 60, 90, 180].map((d) => (
                <option key={d} value={d}>
                  {d} يومًا
                </option>
              ))}
            </Select>
          </Field>
        </div>
      )}

      {/* ⚠ والشاشةُ تقول الحدَّ قبل أن يقوله الخادمُ رفضًا — راحةٌ لا حراسة. */}
      {tooLong && (
        <Banner tone="warn">
          المدى {span} يومًا، والحدُّ الأقصى <b>٣٦٦</b>. اقسِمه أو صدّره على دفعات.
        </Banner>
      )}

      {inverted && <Banner tone="warn">تاريخُ البداية بعد تاريخ النهاية.</Banner>}

      {!tooLong && !inverted && (
        <Report
          tab={tab}
          from={from}
          to={to}
          staleDays={staleDays}
          canExport={canExport}
        />
      )}
    </div>
  )
}

const PATHS: Record<Tab, string> = {
  sales: 'sales',
  invoices: 'invoices',
  top: 'top-products',
  margin: 'margin',
  stock: 'stock-alerts',
  shifts: 'shifts',
  purchases: 'purchases',
}

function Report({
  tab,
  from,
  to,
  staleDays,
  canExport,
}: {
  tab: Tab
  from: string
  to: string
  staleDays: number
  canExport: boolean
}) {
  const path = PATHS[tab]

  const query = tab === 'stock'
    ? `staleDays=${staleDays}`
    : `from=${from}&to=${to}`

  const result = useQuery({
    queryKey: ['report', tab, query],
    queryFn: () => api<Record<string, unknown>>(`/api/v1/reports/${path}?${query}`),
  })

  if (result.isPending) {
    return <Spinner label="يُحسب…" />
  }

  if (result.error) {
    return <ErrorBanner error={result.error} />
  }

  const data = result.data as never

  return (
    <div className="space-y-3">
      {canExport && (
        <div className="flex justify-end">
          {/* ⚠ **ورابطٌ لا `fetch`**: الملفُّ يُنزَّل بالمتصفّح نفسِه، فلا
              يُحمَّل في الذاكرة ثمّ يُكتب. والرمزُ في الكوكي فيمشي معه. */}
          <a
            className="rounded-lg border border-line px-3 py-2 text-sm hover:bg-surface-raised"
            href={`/api/v1/reports/${path}/export?${query}`}
          >
            ⭳ تصديرٌ إلى Excel
          </a>
        </div>
      )}

      {tab === 'sales' && <SalesTable data={data} />}
      {tab === 'invoices' && <InvoicesTable data={data} />}
      {tab === 'top' && <TopTable data={data} />}
      {tab === 'margin' && <MarginTable data={data} />}
      {tab === 'stock' && <StockTable data={data} />}
      {tab === 'shifts' && <ShiftsTable data={data} />}
      {tab === 'purchases' && <PurchasesTable data={data} />}
    </div>
  )
}

// ── جداول ──────────────────────────────────────────────────────────────────

function Table({ head, children }: { head: string[]; children: React.ReactNode }) {
  return (
    <div className="overflow-x-auto rounded-xl border border-line">
      <table className="w-full text-sm">
        <thead className="bg-surface-raised text-xs text-ink-soft">
          <tr>
            {head.map((h) => (
              <th key={h} className="px-3 py-2 text-right font-semibold">
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>{children}</tbody>
      </table>
    </div>
  )
}

function Num({ children }: { children: React.ReactNode }) {
  return (
    <td className="tabular px-3 py-2 text-right" dir="ltr">
      {children}
    </td>
  )
}

type SalesReport = {
  transactions: number
  revenue: number
  averageBasket: number
  cashIn: number
  nonCashIn: number
  returns: number
  returned: number
  net: number
  days: {
    day: string
    transactions: number
    revenue: number
    averageBasket: number
    cashIn: number
    nonCashIn: number
    returns: number
    returned: number
    net: number
  }[]
  byMethod?: { code: string; name: string; payments: number; amount: number }[] | null
}

function SalesTable({ data }: { data: SalesReport }) {
  return (
    <>
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        <Figure label="الدخل" value={formatMoney(data.revenue)} />
        <Figure label="العمليّات" value={String(data.transactions)} />
        <Figure label="متوسّطُ السلّة" value={formatMoney(data.averageBasket)} />
        <Figure label="الصافي بعد المرتجع" value={formatMoney(data.net)} strong />
      </div>

      {/* ⚠ **وكلُّ طريقة دفعٍ بسطرها** — نقدًا وبطاقةً وكليك وآجلًا. وكان «غيرُ النقد»
          عمودًا واحدًا، والمالكُ يطابق كليك مع إشعارات البنك وحدَه. */}
      {data.byMethod && data.byMethod.length > 0 && (
        <Table head={['طريقةُ الدفع', 'الدفعات', 'المبلغ']}>
          {data.byMethod.map((m) => (
            <tr key={m.code} className="border-t border-line">
              <td className="px-3 py-2 font-medium">{m.name}</td>
              <Num>{m.payments}</Num>
              <Num>{formatMoney(m.amount)}</Num>
            </tr>
          ))}
        </Table>
      )}

      <Table head={['اليوم', 'العمليّات', 'الدخل', 'متوسّطُ السلّة', 'نقدًا', 'غيرُ نقديّ', 'المرتجعات', 'قيمتُها', 'الصافي']}>
        {data.days.map((d) => (
          <tr key={d.day} className="border-t border-line">
            <td className="tabular px-3 py-2" dir="ltr">{d.day}</td>
            <Num>{d.transactions}</Num>
            <Num>{formatMoney(d.revenue)}</Num>
            <Num>{formatMoney(d.averageBasket)}</Num>
            <Num>{formatMoney(d.cashIn)}</Num>
            <Num>{formatMoney(d.nonCashIn)}</Num>
            <Num>{d.returns}</Num>
            <Num>{formatMoney(d.returned)}</Num>
            <Num>{formatMoney(d.net)}</Num>
          </tr>
        ))}
      </Table>

      {/* ⚠ **والمرتجعُ في يوم إرجاعه** (ب-٢) — يُقال هنا لأنّ قارئًا يجمع
          عمودَ الدخل قد يتساءل لماذا لا يطابق يومَ البيع. */}
      <p className="text-xs text-ink-soft">
        والمرتجعُ محسوبٌ <b>في يوم إرجاعه</b> لا في يوم بيعه — فتقريرُ يومٍ مضى
        لا يتغيّر بعد أن يُقرأ.
      </p>
    </>
  )
}

type TopRow = {
  variantId: string
  sku: string
  productName: string
  brandName: string | null
  categoryName: string | null
  qty: number
  revenue: number
  saleCount: number
  returnedQty: number
}

type CategoryRow = {
  categoryName: string
  qty: number
  revenue: number
  products: number
  sharePercent: number
}

function TopTable({
  data,
}: {
  data: { byQty: TopRow[]; byRevenue: TopRow[]; byCategory: CategoryRow[] }
}) {
  return (
    <div className="space-y-5">
      {/* ⚠ **والقسمُ يُجيب سؤالًا لا تجيبه قائمةُ الأصناف**: عشرون كفرًا تبيع
          واحدًا واحدًا لا يتصدّر أيٌّ منها، ومجموعُها قد يفوق كلَّ السمّاعات. */}
      {data.byCategory.length > 0 && (
        <div className="space-y-2">
          <h2 className="text-sm font-semibold">بالقسم</h2>

          <Table head={['القسم', 'الأصناف', 'الكميّة', 'الدخل', 'الحصّة']}>
            {data.byCategory.map((c) => (
              <tr key={c.categoryName} className="border-t border-line">
                <td className="px-3 py-2 font-medium">{c.categoryName}</td>
                <Num>{c.products}</Num>
                <Num>{formatQty(c.qty)}</Num>
                <Num>{formatMoney(c.revenue)}</Num>
                <Num>{c.sharePercent.toFixed(1)}٪</Num>
              </tr>
            ))}
          </Table>
        </div>
      )}

      <div className="grid gap-4 lg:grid-cols-2">
      {[
        { title: 'بالكميّة', rows: data.byQty },
        { title: 'بالقيمة', rows: data.byRevenue },
      ].map((side) => (
        <div key={side.title} className="space-y-2">
          <h2 className="text-sm font-semibold">{side.title}</h2>

          {side.rows.length === 0 ? (
            <Empty>لا بيعَ في هذا المدى.</Empty>
          ) : (
            <Table head={['الصنف', 'القسم', 'الكميّة', 'الدخل', 'عاد']}>
              {side.rows.map((r) => (
                <tr key={r.variantId} className="border-t border-line">
                  <td className="px-3 py-2">
                    <div>{r.productName}</div>
                    <div className="tabular text-xs text-ink-soft" dir="ltr">{r.sku}</div>
                  </td>
                  <td className="px-3 py-2 text-xs">
                    {r.categoryName ?? <span className="text-warn">بلا قسم</span>}
                  </td>
                  <Num>{formatQty(r.qty)}</Num>
                  <Num>{formatMoney(r.revenue)}</Num>
                  <Num>{r.returnedQty > 0 ? formatQty(r.returnedQty) : '—'}</Num>
                </tr>
              ))}
            </Table>
          )}
        </div>
      ))}
      </div>
    </div>
  )
}

type MarginReport = {
  revenue: number
  knownRevenue: number
  knownCost: number
  knownMargin: number
  knownMarginPercent: number | null
  uncostedLines: number
  uncostedRevenue: number
  /** ما رُدّ للزبائن في المدى — <b>مطروحٌ أصلًا من الأرقام أعلاه</b>. */
  returnedRevenue: number
  returnedCost: number
  days: {
    day: string
    revenue: number
    knownRevenue: number
    knownCost: number
    knownMargin: number
    uncostedLines: number
    uncostedRevenue: number
    returnedRevenue: number
    returnedCost: number
  }[]
}

/**
 * ⚠ <b>ولا يُسمّى الرقمُ «الربح»</b> — قرارُ المالك في ب-٣.
 *
 * <b>وهو هامشٌ على السطور ذاتِ الكلفة المعروفة وحدَها.</b> وما لا كلفةَ له
 * يُعرض بجانبه صراحةً — لا يُعدّ صفرًا فيرفع الرقمَ كذبًا، ولا يُسقط التقريرَ
 * كلَّه فيمنع جوابًا صحيحًا عن بقيّته.
 */
function MarginTable({ data }: { data: MarginReport }) {
  return (
    <>
      {/* ⚠ **والأرقامُ كلُّها صافيةٌ بعد المرتجع** — قرارُ المالك ٢٠٢٦-٠٩-٢٤.
          والمرتجعُ يُطرح من الطرفين: بيعةٌ بعشرةٍ كلفتُها ستّةٌ ثمّ رُدّت،
          طرحُ دخلِها وحدَه يُنتج هامشًا **سالبًا بستّة** عن صفقةٍ لم تقع. */}
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        <Figure label="الدخلُ صافيًا" value={formatMoney(data.revenue)} />
        <Figure label="دخلٌ تُعرف كلفتُه" value={formatMoney(data.knownRevenue)} />
        <Figure label="الكلفةُ المعروفة" value={formatMoney(data.knownCost)} />
        <Figure
          label="الهامشُ المعروف"
          value={
            formatMoney(data.knownMargin) +
            (data.knownMarginPercent === null ? '' : ` · ${data.knownMarginPercent.toFixed(1)}٪`)
          }
          strong
        />
      </div>

      {data.returnedRevenue > 0 && (
        <Banner tone="info">
          خُصم المرتجع: <b>{formatMoney(data.returnedRevenue)}</b> من الدخل
          {data.returnedCost > 0 && <> و<b>{formatMoney(data.returnedCost)}</b> من الكلفة</>}
          {' '}— <b>والبضاعةُ عادت إلى الرفّ فكلفتُها لم تُستهلَك</b>.
          وبيومِ الترحيل لا بيوم البيع، فلا يتغيّر رقمُ يومٍ مضى.
        </Banner>
      )}

      {data.uncostedLines > 0 ? (
        <Banner tone="warn">
          <b>{data.uncostedLines} سطرًا بلا كلفة</b>، ودخلُها{' '}
          {formatMoney(data.uncostedRevenue)}. وهي <b>خارجَ الهامش</b> أعلاه —
          فالرقمُ محسوبٌ على ما تُعرف كلفتُه وحدَه، ولا يُقرأ ربحًا للمدى كلِّه.
        </Banner>
      ) : (
        <Banner tone="ok">وكلُّ سطرٍ في هذا المدى تُعرف كلفتُه — فالهامشُ كاملٌ.</Banner>
      )}

      <Table head={['اليوم', 'الدخل', 'مرتجع', 'دخلٌ بكلفة', 'الكلفة', 'الهامش', 'سطورٌ بلا كلفة', 'دخلُها']}>
        {data.days.map((d) => (
          <tr key={d.day} className="border-t border-line">
            <td className="tabular px-3 py-2" dir="ltr">{d.day}</td>
            <Num>{formatMoney(d.revenue - d.returnedRevenue)}</Num>
            <Num>
              {d.returnedRevenue > 0
                ? <span className="text-warn">{formatMoney(d.returnedRevenue)}</span>
                : '—'}
            </Num>
            <Num>{formatMoney(d.knownRevenue - d.returnedRevenue)}</Num>
            <Num>{formatMoney(d.knownCost - d.returnedCost)}</Num>
            <Num>{formatMoney(d.knownRevenue - d.returnedRevenue - (d.knownCost - d.returnedCost))}</Num>
            <Num>{d.uncostedLines || '—'}</Num>
            <Num>{d.uncostedRevenue > 0 ? formatMoney(d.uncostedRevenue) : '—'}</Num>
          </tr>
        ))}
      </Table>
    </>
  )
}

type AlertRow = {
  variantId: string
  sku: string
  productName: string
  warehouseName: string
  qtyOnHand: number
  stockValue: number | null
  daysSinceSold: number | null
  allowNegativeStock: boolean
}

function StockTable({
  data,
}: {
  data: { staleDays: number; stale: AlertRow[]; outOfStock: AlertRow[]; negative: AlertRow[] }
}) {
  const sections = [
    { title: `راكدٌ — لم يُبَع منذ ${data.staleDays} يومًا وله رصيد`, rows: data.stale, tone: 'warn' as const },
    { title: 'سالبٌ — الدفترُ لا يطابق الرفّ', rows: data.negative, tone: 'warn' as const },
    { title: 'نافدٌ — رصيدُه صفرٌ وقد كان يُباع', rows: data.outOfStock, tone: 'info' as const },
  ]

  return (
    <div className="space-y-5">
      {sections.map((section) => (
        <div key={section.title} className="space-y-2">
          <h2 className="text-sm font-semibold">
            {section.title}
            <span className="tabular mr-2 text-xs text-ink-soft" dir="ltr">
              {section.rows.length}
            </span>
          </h2>

          {section.rows.length === 0 ? (
            <Empty>لا شيءَ هنا — وهذا خبرٌ جيّد.</Empty>
          ) : (
            <Table head={['الصنف', 'المستودع', 'الرصيد', 'قيمتُه', 'أيّامٌ بلا بيع']}>
              {section.rows.map((r) => (
                <tr key={`${r.variantId}-${r.warehouseName}`} className="border-t border-line">
                  <td className="px-3 py-2">
                    <div>{r.productName}</div>
                    <div className="tabular text-xs text-ink-soft" dir="ltr">{r.sku}</div>
                  </td>
                  <td className="px-3 py-2">{r.warehouseName}</td>
                  <Num>
                    <span className={r.qtyOnHand < 0 ? 'font-bold text-warn' : ''}>
                      {formatQty(r.qtyOnHand)}
                    </span>
                    {r.allowNegativeStock && r.qtyOnHand < 0 && (
                      <span className="mr-1 text-xs text-ink-soft">(مأذون)</span>
                    )}
                  </Num>
                  <Num>{r.stockValue === null ? '—' : formatMoney(r.stockValue)}</Num>
                  <Num>{r.daysSinceSold ?? '—'}</Num>
                </tr>
              ))}
            </Table>
          )}
        </div>
      ))}
    </div>
  )
}

type ShiftRow = {
  sessionId: string
  sessionNo: string
  cashierName: string
  day: string
  status: string
  openingCash: number
  expectedCash: number | null
  countedCash: number | null
  difference: number | null
  closeReason: string | null
  salesCount: number
  salesTotal: number
}

/**
 * كشفُ الفواتير — <b>السطرُ الذي يُطابَق به الدرجُ والدفتر</b>.
 *
 * ⚠ <b>والمجاميعُ وحدَها لا تكفي</b>: من يرى «٤٤ دينارًا اليوم» ولا يرى
 *   الفواتيرَ لا يستطيع أن يسأل «أيُّ فاتورةٍ هذه؟».
 */
function InvoicesTable({
  data,
}: {
  data: {
    count: number
    total: number
    rows: {
      saleNo: string
      completedAt: string
      cashierName: string
      customerName: string | null
      lines: number
      discount: number
      total: number
      items: {
        productName: string
        sku: string
        qty: number
        unitPrice: number
        discount: number
        lineTotal: number
      }[]
      payments: { methodName: string; amount: number }[]
    }[]
  }
}) {
  return (
    <>
      <div className="grid grid-cols-2 gap-2">
        <Figure label="عددُ الفواتير" value={String(data.count)} />
        <Figure label="مجموعُها" value={formatMoney(data.total)} />
      </div>

      <Table head={['الفاتورة', 'الوقت', 'الموظّف', 'الزبون', 'سطور', 'خصم', 'المجموع']}>
        {data.rows.map((row) => (
          <Fragment key={row.saleNo}>
            <tr className="border-t-2 border-line">
              <td className="tabular px-3 py-2 font-semibold" dir="ltr">{row.saleNo}</td>
              <td className="px-3 py-2 text-xs text-ink-soft">{formatMoment(row.completedAt)}</td>
              <td className="px-3 py-2">{row.cashierName}</td>
              <td className="px-3 py-2 text-ink-soft">{row.customerName ?? '—'}</td>
              <td className="tabular px-3 py-2" dir="ltr">{row.lines}</td>
              <td className="tabular px-3 py-2" dir="ltr">
                {row.discount > 0 ? <span className="text-warn">{formatMoney(row.discount)}</span> : '—'}
              </td>
              <td className="tabular px-3 py-2 font-bold" dir="ltr">{formatMoney(row.total)}</td>
            </tr>

            {/* ⚠ **وتحت كلّ فاتورةٍ موادُّها وطرقُ دفعها** — طلبُ المالك
                ٢٠٢٦-٠٩-٢٨. والمجموعُ وحدَه لا يُجيب «ماذا بيع؟» ولا «كيف دُفع؟». */}
            <tr>
              <td colSpan={7} className="bg-surface px-3 pb-3 pt-1">
                <ul className="grid gap-0.5 text-xs">
                  {row.items.map((item, index) => (
                    <li key={index} className="flex items-baseline gap-3">
                      <span className="tabular w-10 shrink-0 text-ink-soft" dir="ltr">×{formatQty(item.qty)}</span>
                      <span className="min-w-0 flex-1 truncate">{item.productName}</span>
                      <span className="tabular shrink-0 text-ink-soft" dir="ltr">{formatMoney(item.unitPrice)}</span>
                      {item.discount > 0 && (
                        <span className="shrink-0 text-warn">خصم {formatMoney(item.discount)}</span>
                      )}
                      <span className="tabular w-24 shrink-0 text-left font-semibold" dir="ltr">
                        {formatMoney(item.lineTotal)}
                      </span>
                    </li>
                  ))}
                </ul>

                <div className="mt-1.5 flex flex-wrap gap-1 text-xs">
                  {row.payments.map((payment) => (
                    <span key={payment.methodName} className="rounded bg-surface-raised px-2 py-0.5 text-ink-soft">
                      {payment.methodName}{' '}
                      <b className="tabular" dir="ltr">{formatMoney(payment.amount)}</b>
                    </span>
                  ))}
                </div>
              </td>
            </tr>
          </Fragment>
        ))}
      </Table>

      {data.rows.length === 0 && (
        <p className="mt-2 text-sm text-ink-soft">لا فواتيرَ في هذا المدى.</p>
      )}
    </>
  )
}

function ShiftsTable({
  data,
}: {
  data: {
    shifts: number
    closed: number
    totalDifference: number
    shortageTotal: number
    surplusTotal: number
    rows: ShiftRow[]
    byCashier: {
      cashierId: string
      cashierName: string
      shifts: number
      shortageTotal: number
      surplusTotal: number
      shiftsWithDifference: number
    }[]
  }
}) {
  return (
    <>
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        <Figure label="ورديّات" value={String(data.shifts)} />
        <Figure label="أُغلقت" value={String(data.closed)} />
        <Figure label="مجموعُ النقص" value={formatMoney(data.shortageTotal)} />
        <Figure label="مجموعُ الزيادة" value={formatMoney(data.surplusTotal)} />
      </div>

      {/* ⚠ **والنقصُ والزيادةُ لا يُلغي أحدُهما الآخر**: مجموعٌ صفرٌ عن يومٍ
          فيه خطآن يقول «كلُّ شيءٍ بخير». */}
      <p className="text-xs text-ink-soft">
        والنقصُ والزيادةُ مجموعان <b>منفصلَين</b> عمدًا — ورديّةٌ ناقصةٌ وأخرى
        زائدةٌ مجموعُهما صفر، وهو رقمٌ يُخفي خطأين.
      </p>

      {data.byCashier.length > 0 && (
        <Table head={['الكاشير', 'ورديّات', 'بفرق', 'النقص', 'الزيادة']}>
          {data.byCashier.map((c) => (
            <tr key={c.cashierId} className="border-t border-line">
              <td className="px-3 py-2 font-medium">{c.cashierName}</td>
              <Num>{c.shifts}</Num>
              <Num>{c.shiftsWithDifference}</Num>
              <Num>{formatMoney(c.shortageTotal)}</Num>
              <Num>{formatMoney(c.surplusTotal)}</Num>
            </tr>
          ))}
        </Table>
      )}

      <Table head={['الورديّة', 'الكاشير', 'اليوم', 'الحالة', 'الافتتاحيّ', 'المتوقَّع', 'المعدود', 'الفرق', 'السبب', 'بيعات']}>
        {data.rows.map((s) => (
          <tr key={s.sessionId} className="border-t border-line">
            <td className="tabular px-3 py-2" dir="ltr">{s.sessionNo}</td>
            <td className="px-3 py-2">{s.cashierName}</td>
            <td className="tabular px-3 py-2" dir="ltr">{s.day}</td>
            <td className="px-3 py-2 text-xs">{s.status === 'OPEN' ? 'مفتوحة' : 'مُغلقة'}</td>
            {/* بكم فُتح الدرج — أوّلُ ما يُسأل عنه حين لا يطابق المعدودُ المتوقَّع. */}
            <Num>{formatMoney(s.openingCash)}</Num>
            <Num>{s.expectedCash === null ? '—' : formatMoney(s.expectedCash)}</Num>
            <Num>{s.countedCash === null ? '—' : formatMoney(s.countedCash)}</Num>
            <Num>
              {s.difference === null ? (
                '—'
              ) : (
                <span
                  className={
                    s.difference === 0 ? '' : s.difference < 0 ? 'font-bold text-warn' : 'font-bold text-ok'
                  }
                >
                  {formatMoney(s.difference)}
                </span>
              )}
            </Num>
            <td className="px-3 py-2 text-xs">{s.closeReason ?? ''}</td>
            <Num>{s.salesCount}</Num>
          </tr>
        ))}
      </Table>
    </>
  )
}

function PurchasesTable({
  data,
}: {
  data: {
    receipts: number
    totalQty: number
    totalCost: number
    bySupplier: {
      supplierId: string
      supplierCode: string
      supplierName: string
      receipts: number
      totalQty: number
      totalCost: number
    }[]
    rows: {
      receiptId: string
      receiptNo: string
      day: string
      supplierName: string
      supplierInvoiceNo: string | null
      totalQty: number
      totalCost: number
    }[]
  }
}) {
  return (
    <>
      <div className="grid grid-cols-3 gap-2">
        <Figure label="إيصالات" value={String(data.receipts)} />
        <Figure label="الكميّة" value={formatQty(data.totalQty)} />
        <Figure label="الكلفة" value={formatMoney(data.totalCost)} strong />
      </div>

      {data.bySupplier.length > 0 && (
        <Table head={['المورّد', 'إيصالات', 'الكميّة', 'الكلفة']}>
          {data.bySupplier.map((s) => (
            <tr key={s.supplierId} className="border-t border-line">
              <td className="px-3 py-2">
                {s.supplierName}
                <span className="tabular mr-2 text-xs text-ink-soft" dir="ltr">{s.supplierCode}</span>
              </td>
              <Num>{s.receipts}</Num>
              <Num>{formatQty(s.totalQty)}</Num>
              <Num>{formatMoney(s.totalCost)}</Num>
            </tr>
          ))}
        </Table>
      )}

      <Table head={['الإيصال', 'اليوم', 'المورّد', 'فاتورتُه', 'الكميّة', 'الكلفة']}>
        {data.rows.map((r) => (
          <tr key={r.receiptId} className="border-t border-line">
            <td className="tabular px-3 py-2" dir="ltr">{r.receiptNo}</td>
            <td className="tabular px-3 py-2" dir="ltr">{r.day}</td>
            <td className="px-3 py-2">{r.supplierName}</td>
            <td className="tabular px-3 py-2 text-xs" dir="ltr">{r.supplierInvoiceNo ?? ''}</td>
            <Num>{formatQty(r.totalQty)}</Num>
            <Num>{formatMoney(r.totalCost)}</Num>
          </tr>
        ))}
      </Table>
    </>
  )
}

function Figure({ label, value, strong }: { label: string; value: string; strong?: boolean }) {
  return (
    <div className="rounded-xl border border-line bg-surface-raised p-3">
      <div className="text-xs text-ink-soft">{label}</div>
      <div className={`tabular mt-1 ${strong ? 'text-xl font-bold' : 'text-lg'}`} dir="ltr">
        {value}
      </div>
    </div>
  )
}
